import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { NbxCapacitorFirebaseAuth } from '@nbx/capacitor-firebase-auth';

import { Capacitor } from '@capacitor/core';

import { NBXStorage } from '@nbx/frontend-helpers/storage';
import NBXUsers from '../users.service';

import { requireProvider, requireUser } from './helpers';

import type { FirebaseContext } from './context';
import type { FirebaseAuthModule, FirebaseProviderMethods, ReauthenticateOptions } from './types';

interface FirebaseProviderDeps {
  importAuth: () => Promise<FirebaseAuthModule>;
  user: () => Promise<import('firebase/auth').User | null>;
  signInWithCredential: (
    firebaseCredential: import('firebase/auth').AuthCredential,
    reauthenticate: boolean
  ) => Promise<unknown>;
}

export function createFirebaseProviderMethods(
  context: FirebaseContext,
  deps: FirebaseProviderDeps
): FirebaseProviderMethods {
  const { log, vippsScopes, state } = context;

  return {
    signInWithVipps: async ({ reauthenticate }: ReauthenticateOptions = { reauthenticate: false }) => {
      log.info('signInWithVipps reauthenticate =', reauthenticate);
      const firebaseAuth = await deps.importAuth();
      let vippsAccessToken: string | undefined;
      let result: unknown;

      if (Capacitor.isNativePlatform()) {
        log.info('vipss OAuth Native, signInWithOAuthProvider');
        const nativeCredential = reauthenticate
          ? await NbxCapacitorFirebaseAuth.reauthenticateWithOAuthProvider({
              providerId: 'oidc.vipps',
              scopes: vippsScopes.split(' ')
            })
          : await NbxCapacitorFirebaseAuth.signInWithOAuthProvider({
              providerId: 'oidc.vipps',
              scopes: vippsScopes.split(' ')
            });

        log.info('signInWithVipps firebaseAuthResult=', !!nativeCredential);
        log.info(
          'accessToken',
          !!(nativeCredential as { credential?: { accessToken?: string } })?.credential?.accessToken
        );
        vippsAccessToken = (nativeCredential as { credential?: { accessToken?: string } })?.credential
          ?.accessToken;
        result = nativeCredential;
      } else {
        const provider = requireProvider(state.vippsOpenIDConnectProvider, 'Vipps');

        if (reauthenticate) {
          result = await firebaseAuth.reauthenticateWithPopup(
            await requireUser(deps.user, 'reauthenticate with Vipps'),
            provider
          );
        } else {
          result = await firebaseAuth.signInWithPopup(await firebaseAuth.getAuth(), provider);
        }

        const { accessToken } =
          firebaseAuth.OAuthProvider.credentialFromResult(
            result as Parameters<typeof firebaseAuth.OAuthProvider.credentialFromResult>[0]
          ) ?? {};
        log.info('signInWithVipps firebaseAuthResult=', !!result);
        vippsAccessToken = accessToken ?? undefined;
      }

      if (!reauthenticate && vippsAccessToken) {
        NBXUsers.vippsUserInfo(vippsAccessToken)
          .then(async userInfo => {
            log.info('vippsLogin', 'userInfo', JSON.stringify(userInfo));
            await NBXStorage.setItem('vippsUserInfo', JSON.stringify(userInfo));
            log.info('vippsLogin', 'stored user info');
          })
          .catch(error => {
            log.error('vippsLogin', error);
          });
      }

      return result;
    },

    signInWithGoogle: async ({ reauthenticate }: ReauthenticateOptions = { reauthenticate: false }) => {
      log.info('signInWithGoogle reauthenticate =', reauthenticate);
      const firebaseAuth = await deps.importAuth();

      if (Capacitor.getPlatform() === 'android') {
        log.info('google OAuth Native');
        const authResult = reauthenticate
          ? await NbxCapacitorFirebaseAuth.reauthenticateWithOAuthProvider({ providerId: 'google.com' })
          : await NbxCapacitorFirebaseAuth.signInWithOAuthProvider({ providerId: 'google.com' });
        log.info('signInWithGoogle authResult=', !!authResult);
        return authResult;
      }

      if (Capacitor.isNativePlatform()) {
        log.info('google OAuth: skipping native firebase sign-in to prevent native firebase mfa trigger.');
        const oAuthResult = await FirebaseAuthentication.signInWithGoogle({
          skipNativeAuth: true
        });
        log.info('signInWithGoogle oAuthResult=', !!oAuthResult);
        const firebaseCredential = firebaseAuth.GoogleAuthProvider.credential(
          oAuthResult.credential?.idToken
        );
        return deps.signInWithCredential(firebaseCredential, reauthenticate);
      }

      const provider = requireProvider(state.googleOpenIDConnectProvider, 'Google');
      if (reauthenticate) {
        return firebaseAuth.reauthenticateWithPopup(
          await requireUser(deps.user, 'reauthenticate with Google'),
          provider
        );
      }

      return firebaseAuth.signInWithPopup(await firebaseAuth.getAuth(), provider);
    },

    signInWithApple: async ({ reauthenticate }: ReauthenticateOptions = { reauthenticate: false }) => {
      log.info('signInWithApple reauthenticate =', reauthenticate);
      const firebaseAuth = await deps.importAuth();

      if (Capacitor.getPlatform() === 'ios') {
        let oAuthResult: Awaited<ReturnType<typeof FirebaseAuthentication.signInWithApple>>;

        try {
          log.info('apple OAuth: skipping native firebase sign-in to prevent native firebase mfa trigger.');
          oAuthResult = await FirebaseAuthentication.signInWithApple({
            skipNativeAuth: true
          });
        } catch (error) {
          log.error('cannot FirebaseAuthentication.signInWithApple', error);
          throw error;
        }

        log.info('signInWithApple oAuthResult=', !!oAuthResult);
        const provider = requireProvider(state.appleOpenIDConnectProvider, 'Apple');
        const firebaseCredential = provider.credential({
          ...oAuthResult.credential,
          rawNonce: oAuthResult.credential?.nonce
        });
        log.info('signInWithApple signInWithCredential', !!firebaseCredential);
        return deps.signInWithCredential(firebaseCredential, reauthenticate);
      }

      if (Capacitor.getPlatform() === 'android') {
        log.info('apple OAuth Native');
        const authResult = reauthenticate
          ? await NbxCapacitorFirebaseAuth.reauthenticateWithOAuthProvider({ providerId: 'apple.com' })
          : await NbxCapacitorFirebaseAuth.signInWithOAuthProvider({ providerId: 'apple.com' });
        log.info('signInWithApple authResult=', !!authResult);
        return authResult;
      }

      const provider = requireProvider(state.appleOpenIDConnectProvider, 'Apple');
      if (reauthenticate) {
        return firebaseAuth.reauthenticateWithPopup(
          await requireUser(deps.user, 'reauthenticate with Apple'),
          provider
        );
      }

      return firebaseAuth.signInWithPopup(await firebaseAuth.getAuth(), provider);
    }
  };
}
